/**
 * <s-product-gallery> — product page media viewer.
 *
 * The main viewer is a native horizontal scroll-snap track, so swiping and
 * keyboard scrolling work without JavaScript. This component adds:
 *   - `--previous` / `--next` commands from buttons using commandfor,
 *   - thumbnail links (a[data-gallery-thumb][href="#slide-id"]) that scroll the
 *     track sideways instead of jumping the page,
 *   - aria-current on the thumbnail of the visible slide,
 *   - jumping to the selected variant's image when a product option changes
 *     (script[data-variant-media] maps variant id → slide id; data-initial-slide
 *     opens on the deep-linked variant's image).
 *
 * Required markup:
 *   [data-gallery-track]          — the scroll-snap container.
 *   [data-gallery-slide][id]      — each slide inside the track.
 *   a[data-gallery-thumb][href]   — optional thumbnails pointing at slide ids.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #variantMedia = {};

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', this.#handleCommand, opts);
    this.addEventListener('click', this.#handleThumbClick, opts);

    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#markActive(entry.target.id);
        }
      },
      { root: track, threshold: 0.6 },
    );

    for (const slide of this.#slides()) this.#observer.observe(slide);

    // Variant → image map rendered by the template.
    try {
      const mapScript = this.querySelector('script[data-variant-media]');
      this.#variantMedia = mapScript ? JSON.parse(mapScript.textContent) : {};
    } catch {
      this.#variantMedia = {};
    }

    // Option inputs live outside this element (in the product form), so listen at document level.
    // Same detection as s-variant-picker: data-variant-id on the input or on the selected <option>.
    document.addEventListener('change', this.#handleVariantChange, opts);

    const initialSlide = this.dataset.initialSlide;
    if (initialSlide) {
      requestAnimationFrame(() => this.#goToSlideId(initialSlide, true));
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #slides() {
    return [...this.querySelectorAll('[data-gallery-slide]')];
  }

  #currentIndex() {
    const active = this.querySelector('[data-gallery-thumb][aria-current="true"]');
    const id = active?.getAttribute('href')?.slice(1);
    const index = this.#slides().findIndex((slide) => slide.id === id);
    return index < 0 ? 0 : index;
  }

  #goToSlideId(id, instant = false) {
    const index = this.#slides().findIndex((slide) => slide.id === id);
    if (index >= 0) this.#goTo(index, instant);
  }

  #handleVariantChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
    const variantId = input.dataset.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
    if (!variantId) return;
    const slideId = this.#variantMedia[variantId];
    if (slideId) this.#goToSlideId(slideId);
  };

  #goTo(index, instant = false) {
    const slides = this.#slides();
    if (!slides.length) return;
    const wrapped = (index + slides.length) % slides.length;
    const slide = slides[wrapped];
    const track = this.querySelector('[data-gallery-track]');
    const reduce = instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    track?.scrollTo({ left: direction * wrapped * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    this.#markActive(slide.id);
  }

  #markActive(id) {
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      const isActive = thumb.getAttribute('href') === `#${id}`;
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      if (isActive) this.#revealThumb(thumb);
    }
  }

  /** Scrolls the thumbnail strip (not the page) so the active thumbnail is visible. */
  #revealThumb(thumb) {
    const list = thumb.closest('[data-gallery-thumbs]');
    if (!list) return;
    const item = thumb.parentElement ?? thumb;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const listBox = list.getBoundingClientRect();
    const itemBox = item.getBoundingClientRect();
    if (list.scrollHeight > list.clientHeight) {
      if (itemBox.top < listBox.top) list.scrollBy({ top: itemBox.top - listBox.top, behavior });
      else if (itemBox.bottom > listBox.bottom) list.scrollBy({ top: itemBox.bottom - listBox.bottom, behavior });
    } else if (list.scrollWidth > list.clientWidth) {
      if (itemBox.left < listBox.left) list.scrollBy({ left: itemBox.left - listBox.left, behavior });
      else if (itemBox.right > listBox.right) list.scrollBy({ left: itemBox.right - listBox.right, behavior });
    }
  }

  #handleCommand = (event) => {
    if (event.command === '--previous') this.#goTo(this.#currentIndex() - 1);
    if (event.command === '--next') this.#goTo(this.#currentIndex() + 1);
  };

  #handleThumbClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('a[data-gallery-thumb]');
    if (!thumb || !this.contains(thumb)) return;
    event.preventDefault();
    const id = thumb.getAttribute('href').slice(1);
    const index = this.#slides().findIndex((slide) => slide.id === id);
    if (index >= 0) this.#goTo(index);
  };
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
